import React, { useState, useEffect } from 'react';
import { FolderOpen, FileText, ChevronRight, ArrowLeft, X, Download, Home } from 'lucide-react';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileColor(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  const colors = { pdf: '#EF4444', doc: '#3B82F6', docx: '#3B82F6', xls: '#10B981', xlsx: '#10B981', ppt: '#F59E0B', pptx: '#F59E0B', zip: '#8B5CF6', jpg: '#EC4899', png: '#EC4899' };
  return colors[ext] || '#6B7280';
}

export default function DropboxFilePicker({ open, onClose, onSelect, apiFetch }) {
  const [currentPath, setCurrentPath] = useState('');
  const [dlRoot, setDlRoot] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) loadContent('');
  }, [open]);

  const loadContent = async (path) => {
    setLoading(true);
    setError('');
    const url = path ? `/api/downloads/categories?path=${encodeURIComponent(path)}` : '/api/downloads/categories';
    try {
      const r = await apiFetch(url);
      if (r?.ok) {
        const d = await r.json();
        setFolders(d.folders || []);
        setFiles(d.files || []);
        setCurrentPath(d.path || '');
        setDlRoot(d.root || '');
      } else {
        const err = await r?.json().catch(() => null);
        setError(err?.detail || 'Fehler');
      }
    } catch { setError('Nicht erreichbar'); }
    setLoading(false);
  };

  const navigateTo = (path) => { setCurrentPath(path); loadContent(path); };

  const navigateUp = () => {
    if (!currentPath || currentPath === dlRoot) return;
    const parent = currentPath.substring(0, currentPath.lastIndexOf('/'));
    if (parent.length < dlRoot.length) return;
    navigateTo(parent);
  };

  const getBreadcrumbs = () => {
    if (!currentPath || !dlRoot) return [];
    const relative = currentPath.startsWith(dlRoot) ? currentPath.slice(dlRoot.length) : '';
    if (!relative) return [];
    return relative.split('/').filter(Boolean).map((seg, i, arr) => ({
      name: seg,
      path: dlRoot + '/' + arr.slice(0, i + 1).join('/'),
    }));
  };

  const breadcrumbs = getBreadcrumbs();
  const isAtRoot = !currentPath || currentPath === dlRoot;

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose} data-testid="file-picker-overlay">
      <div style={{
        background: 'var(--bg-primary, #fff)', borderRadius: 12, width: '100%', maxWidth: 560,
        maxHeight: '70vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }} onClick={e => e.stopPropagation()} data-testid="file-picker-modal">
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Dokument einfügen</h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Breadcrumb */}
        <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 12 }}>
          <button onClick={() => navigateTo('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', color: 'var(--primary)', fontWeight: isAtRoot ? 600 : 400, fontSize: 12, borderRadius: 4 }}>
            <Home size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} /> Downloads
          </button>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={bc.path}>
              <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
              <button onClick={() => i < breadcrumbs.length - 1 && navigateTo(bc.path)}
                style={{ background: 'none', border: 'none', cursor: i < breadcrumbs.length - 1 ? 'pointer' : 'default', padding: '2px 6px', color: i === breadcrumbs.length - 1 ? 'var(--text-primary)' : 'var(--primary)', fontWeight: i === breadcrumbs.length - 1 ? 600 : 400, fontSize: 12, borderRadius: 4 }}>
                {bc.name}
              </button>
            </React.Fragment>
          ))}
          {!isAtRoot && (
            <button className="btn-icon" onClick={navigateUp} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
              <ArrowLeft size={14} />
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Laden...</div>
          ) : error ? (
            <div style={{ padding: 16, color: '#991B1B', fontSize: 13 }}>{error}</div>
          ) : folders.length === 0 && files.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Ordner ist leer</div>
          ) : (
            <>
              {folders.map(folder => (
                <div key={folder.path} onClick={() => navigateTo(folder.path)} data-testid={`pick-folder-${folder.name}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f9fafb)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <FolderOpen size={16} style={{ color: '#F59E0B', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{folder.name}</span>
                  <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                </div>
              ))}
              {folders.length > 0 && files.length > 0 && (
                <div style={{ padding: '6px 20px', background: 'var(--bg-secondary, #f9fafb)', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dateien</div>
              )}
              {files.map(f => (
                <div key={f.path} onClick={() => onSelect(f)} data-testid={`pick-file-${f.name}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#ECFDF5'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: fileColor(f.name) + '15', flexShrink: 0 }}>
                    <FileText size={16} style={{ color: fileColor(f.name) }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatSize(f.size)}</div>
                  </div>
                  <Download size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
